import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { decodeUser } from '../session/session-decode';
import { isIntegrator } from '../session/user-type';
import { decodeAuthorities, hasPerm, Permission } from '../session/permissions';
import { getTenantStorage } from '../session/tenant-storage';
import { subscribeTenantChange } from '../session/tenant-change';
import { isSwitchingAccount } from '../session/account-switch';
import { getAccessToken } from '../../http/auth-storage';
import { closeMobileSidebar, isMobileSidebarOpen, subscribeMobileSidebar } from '../mobile-sidebar';

import { MenuItem } from './core/menu-item';
import { buildMenuContext } from './core/menu-context';
import { buildMenuTree } from './core/sidebar-routes';
import { filterMenuTree } from './core/menu-filter';
import { applyDivisionUnitLabels } from './core/division-unit-labels';
import { getTenantIdentifier } from './core/tenant-identifier';
import { isContaManagementRoute } from './core/conta-management-route';
import { resolveLogoRedirect } from './core/logo-redirect';
import { findActivePath } from './core/active-item';
import { toggleExpandedPath } from './core/expanded-paths';

import {
  useConfigModulos,
  useConfigPsim,
  useConfiguracoes,
  useMulticonta,
  usePreCadastrosAguardando,
  useDocumentoLeiDados,
} from './effects/data';
import { getMulticontaStorage } from './effects/session-storage';
import { cleanFilter, openExternal } from './effects/navigation';
import { useShellSkin } from '../skin/useShellSkin';

// Não há um efeito dedicado para user_instance (mesma observação do useNavbar):
// é a única chave do contrato sem um módulo de storage próprio.
const USER_INSTANCE_KEY = 'user_instance';

export interface SidebarViewModel {
  menu: MenuItem[];
  logoHeader: string;
  logoHeaderReduzido: string;
  collapsed: boolean;
  expandedPaths: ReadonlySet<string>;
  activePath: readonly string[];
  contemPreCadastros: boolean;
  isTransitioning: boolean;
  /** Overlay mobile (abaixo do breakpoint `md`), independente da preferência `collapsed` de desktop. */
  mobileOpen: boolean;
  onToggleCollapse: () => void;
  onToggleSubmenu: (path: string) => void;
  onNavigate: (item: MenuItem) => void;
  onLogoClick: () => void;
  onCloseMobile: () => void;
}

function readLoggedUser() {
  try {
    return decodeUser(sessionStorage.getItem(USER_INSTANCE_KEY));
  } catch {
    return null;
  }
}

/** Costura storage + SWR + núcleo puro + skin num view-model para a Sidebar. */
export function useSidebar(): SidebarViewModel {
  const navigate = useNavigate();

  const [collapsed, setCollapsed] = useState(false);
  const [expandedPaths, setExpandedPaths] = useState<Set<string>>(new Set());
  const [mobileOpen, setMobileOpen] = useState(isMobileSidebarOpen());
  const [, forceRefresh] = useState(0);

  // Navbar e Sidebar são irmãos sem contexto compartilhado; a notificação é o
  // que faz a Sidebar reler o tenant quando a Navbar troca de conta.
  useEffect(() => {
    return subscribeTenantChange(() => forceRefresh((tick) => tick + 1));
  }, []);

  // Mesma limitação de contexto compartilhado: o botão hamburguer vive na
  // Navbar, mas o overlay é renderizado aqui.
  useEffect(() => {
    return subscribeMobileSidebar(() => setMobileOpen(isMobileSidebarOpen()));
  }, []);

  // window.location.hash inclui o caractere '#' (ex.: "#/pessoa/list-pessoa/"), mas
  // item.path não — sem remover o '#' aqui, a comparação exata em findActivePath nunca
  // bate, e o item nunca fica marcado como ativo após a navegação.
  const currentRoute = window.location.hash.replace(/^#/, '');

  const user = readLoggedUser();
  // A Sidebar relê o storage a cada render; na gestão de conta o tenant gravado é a
  // conta em edição, e tratá-lo como conta ativa trocaria o menu do integrador pelo
  // de dentro da conta no primeiro re-render (hover, resposta de SWR).
  const tenant = isContaManagementRoute(currentRoute) ? null : getTenantStorage();
  const token = getAccessToken();
  const authorities = decodeAuthorities(token);
  const possuiTenant = tenant !== null;
  const isIntegrador = isIntegrator(user);
  const identifierTenant = getTenantIdentifier(tenant);

  // Não busca durante a troca de conta: o tenant novo já está no storage mas o
  // token (e as permissões) ainda é o antigo até o refresh assíncrono terminar;
  // buscar aqui traria header/token inconsistentes. O fetch dispara no fim da
  // transição, quando `endAccountSwitchOnTokenRefresh` re-notifica com o token novo.
  const configModulosEnabled =
    possuiTenant && !isSwitchingAccount() && hasPerm(Permission.CONFIGURACOES_READ, authorities);
  const configuracoesEnabled = possuiTenant || hasPerm(Permission.CONFIGURACOES_READ, authorities);

  const multicontaStorageValue = getMulticontaStorage();
  const multicontaEnabled = multicontaStorageValue === null;

  const { configModulos } = useConfigModulos(configModulosEnabled, identifierTenant ?? null);
  // Gate por tenant apenas, sem CONFIGURACOES_READ: o endpoint não exige essa
  // permissão administrativa (várias telas do morador comum chamam getConfig()
  // do PSIM direto) — amarrar ao configModulos gated por permissão deixava a
  // config do PSIM (e o item Timeline) sem carregar para o usuário comum. Mesmo
  // gate de `!isSwitchingAccount()` do configModulos: evita buscar com o tenant
  // novo já no storage mas o token ainda do morador anterior.
  const configPsimEnabled = possuiTenant && !isSwitchingAccount();
  const { configPsim } = useConfigPsim(configPsimEnabled, identifierTenant ?? null);
  const { config } = useConfiguracoes(configuracoesEnabled);
  const { ehMulticonta: ehMulticontaFetched } = useMulticonta(multicontaEnabled);
  const { contemPreCadastros } = usePreCadastrosAguardando(isIntegrador);
  const { lgpdLabel } = useDocumentoLeiDados();

  const ehMulticonta = multicontaStorageValue ?? ehMulticontaFetched;

  const context = buildMenuContext({
    user,
    tenant,
    authorities,
    configModulos,
    ehMulticonta,
    psimHabilitado: configPsim?.habilitar === true,
    webhookHabilitado: configPsim?.webhookHabilitado === true,
    sistemaPSIM: configPsim?.sistemaPSIM ?? null,
    lgpdLabel,
  });

  const menu = applyDivisionUnitLabels(
    filterMenuTree(buildMenuTree(context), identifierTenant),
    config,
  );

  const { logoHeader, logoHeaderReduzido } = useShellSkin();

  const activePath = findActivePath(menu, currentRoute).map((item) => item.path);

  function onToggleCollapse(): void {
    setCollapsed((current) => !current);
  }

  function onToggleSubmenu(path: string): void {
    setExpandedPaths((current) => toggleExpandedPath(current, path));
  }

  function onNavigate(item: MenuItem): void {
    if (!item.path) {
      return;
    }

    cleanFilter();
    // Espelha o clique em item de menu do sidebar legado: em mobile, navegar
    // fecha o overlay (do contrário ele cobriria a tela recém-navegada).
    closeMobileSidebar();

    if (item.isExternalLink) {
      openExternal(item.path);
      return;
    }

    navigate(item.path);
  }

  function onLogoClick(): void {
    closeMobileSidebar();
    const dest = resolveLogoRedirect(user, possuiTenant);
    if (dest) {
      navigate(dest);
    }
  }

  return {
    menu,
    logoHeader,
    logoHeaderReduzido,
    collapsed,
    expandedPaths,
    activePath,
    contemPreCadastros,
    isTransitioning: isSwitchingAccount(),
    mobileOpen,
    onToggleCollapse,
    onToggleSubmenu,
    onNavigate,
    onLogoClick,
    onCloseMobile: closeMobileSidebar,
  };
}
